/**
 * src/pages/ClientProfile.jsx — one client's whole history on a page.
 *
 * The API returns projects, appointments and media in a single response (see
 * GET /api/clients/:id), so this page renders in one round trip rather than
 * four sequential fetches from the browser.
 *
 * Notes are editable in place because that is the field an artist actually
 * changes — "reacts badly to red", "prefers mornings" — and making them open a
 * dialog to do it would mean they simply would not.
 *
 * Owners also get "Export data" and "Delete client" here (LAUNCH.md Phase 4),
 * which is how a studio answers a client's UK GDPR access or erasure request.
 * Artists don't see them; the API refuses them to artists regardless.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useApi } from '../lib/useApi.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import { Empty, ErrorBanner, Loader, PageHeader, Pill } from '../components/ui.jsx';
import { Table, Th, RowHeader } from '../components/ui/index.js';
import { Dialog } from '../components/ui/Dialog.jsx';
import DownloadButton from '../components/DownloadButton.jsx';

export default function ClientProfile() {
  const { id } = useParams();
  const { artist, timezone } = useAuth();
  const { data, error, loading, reload } = useApi(`/api/clients/${id}`);
  const isOwner = artist?.role === 'owner';

  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const client = data?.client;

  const saveNotes = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.patch(`/api/clients/${id}`, { notes: draft });
      setDraft(null);
      reload();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Loader loading={loading} error={error}>
      {client ? (
        <>
          <PageHeader
            title={client.name}
            subtitle={`${client.email}${client.phone ? ` · ${client.phone}` : ''} · client since ${formatDate(client.createdAt, timezone)}`}
            actions={
              <div className="row wrap">
                {isOwner ? (
                  <>
                    <DownloadButton path={`/api/clients/${client.id}/export`} filename={`client-${client.id}.json`} className="btn">
                      Export data
                    </DownloadButton>
                    <button type="button" className="btn btn-danger" onClick={() => setConfirmingDelete(true)}>
                      Delete client
                    </button>
                  </>
                ) : null}
                <Link to="/clients" className="btn">Back to clients</Link>
              </div>
            }
          />
          {confirmingDelete ? (
            <DeleteClientDialog client={client} onClose={() => setConfirmingDelete(false)} />
          ) : null}

          <div className="stack-lg">
            {/* Registration mark: the one field an artist is actually
                editing on this page — see the file header comment on why
                notes are inline rather than behind a dialog. */}
            <section className="card card--current">
              <span className="reg-mark reg-mark--tl" aria-hidden="true" />
              <span className="reg-mark reg-mark--tr" aria-hidden="true" />
              <h2 style={{ marginBottom: 10 }}>Notes</h2>
              <ErrorBanner>{saveError}</ErrorBanner>
              <textarea
                value={draft ?? client.notes ?? ''}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Allergies, skin type, how they sat last time…"
              />
              <div className="row" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={saving || draft === null}
                  onClick={saveNotes}
                >
                  {saving ? 'Saving…' : 'Save notes'}
                </button>
                {draft !== null ? (
                  <button type="button" className="btn btn-sm" onClick={() => setDraft(null)}>
                    Discard
                  </button>
                ) : null}
              </div>
            </section>

            <AftercareEmails client={client} onChanged={reload} />

            <section className="card">
              <h2 style={{ marginBottom: 14 }}>Projects</h2>
              {(data.projects ?? []).length === 0 ? (
                <Empty>No multi-session projects for this client.</Empty>
              ) : (
                <div className="stack">
                  {data.projects.map((p) => (
                    <div key={p.id} className="row-between">
                      <div>
                        <Link to={`/projects/${p.id}`}><strong>{p.title}</strong></Link>
                        <div className="muted small">
                          {p.artist_name}
                          {p.body_location ? ` · ${p.body_location}` : ''}
                        </div>
                      </div>
                      <Pill status={p.status} />
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="card">
              <h2 style={{ marginBottom: 14 }}>Appointments</h2>
              {(data.appointments ?? []).length === 0 ? (
                <Empty>Nothing booked yet.</Empty>
              ) : (
                <Table caption={`${client.name}'s appointments`}>
                  <thead>
                    <tr>
                      <Th>When</Th>
                      <Th>Artist</Th>
                      <Th>Status</Th>
                      <Th>Deposit</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.appointments.map((a) => (
                      <tr key={a.id}>
                        <RowHeader>
                          <span className="mono">{formatDateTime(a.starts_at, timezone)}</span>
                        </RowHeader>
                        <td className="muted">{a.artist_name}</td>
                        <td><Pill status={a.status} /></td>
                        <td className="muted">{a.deposit_paid ? 'Paid' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </section>

            <section className="card">
              <h2 style={{ marginBottom: 14 }}>Photos</h2>
              {(data.media ?? []).length === 0 ? (
                <Empty>No reference or healed photos yet.</Empty>
              ) : (
                <div className="gallery">
                  {data.media.map((m) => (
                    <figure key={m.id} style={{ margin: 0 }}>
                      <img src={m.url} alt={m.asset_type.replace(/_/g, ' ')} loading="lazy" />
                      <figcaption className="muted small" style={{ marginTop: 4 }}>
                        {m.asset_type.replace(/_/g, ' ')} · {formatDate(m.created_at, timezone)}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </section>
          </div>
        </>
      ) : null}
    </Loader>
  );
}

/**
 * Confirms a permanent client deletion.
 *
 * The owner has to type the client's name, not just click "OK": this destroys
 * signed consent forms and photos with no undo, and a typed name is the
 * difference between a deliberate erasure and a misclick on the wrong profile.
 * The API's 409 (an upcoming appointment still booked) is shown as-is.
 *
 * @param {{ client: { id: string, name: string }, onClose: () => void }} props
 * @returns {JSX.Element}
 */
function DeleteClientDialog({ client, onClose }) {
  const navigate = useNavigate();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const matches = typed.trim() === client.name.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!matches) return;
    setBusy(true);
    setError(null);
    try {
      await api.del(`/api/clients/${client.id}`);
      navigate('/clients', { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Dialog title="Delete client" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>
        <p style={{ margin: 0 }}>
          This permanently deletes <strong>{client.name}</strong> and everything linked to them:
          projects, appointments, signed consent forms, photos, aftercare history and inbox
          conversations. It can&rsquo;t be undone.
        </p>
        <p className="muted small" style={{ margin: 0 }}>
          Export their data first if you need to keep a copy.
        </p>
        <div>
          <label htmlFor="confirm-client-name">Type {client.name} to confirm</label>
          <input
            id="confirm-client-name"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="row">
          <button type="submit" className="btn btn-danger" disabled={!matches || busy}>
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Whether the client gets aftercare emails, and a switch for it.
 *
 * Clients turn these off themselves from the link in every aftercare email.
 * Turning them back on is for when the client asks — honouring an
 * unsubscribe is the studio's responsibility (Terms, section 7) — so it asks
 * for confirmation; turning them off doesn't.
 *
 * @param {{ client: { id: string, aftercareEmails: boolean }, onChanged: () => void }} props
 * @returns {JSX.Element}
 */
function AftercareEmails({ client, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const setEnabled = async (aftercareEmails) => {
    if (
      aftercareEmails &&
      !window.confirm('Only turn aftercare emails back on if the client has asked you to. Continue?')
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/clients/${client.id}`, { aftercareEmails });
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="row-between">
        <div>
          <h2 style={{ marginBottom: 4 }}>Aftercare emails</h2>
          <p className="muted small" style={{ margin: 0 }}>
            {client.aftercareEmails
              ? 'On: sent 24 hours, 3 days and 2 weeks after each completed appointment.'
              : 'Off: this client has unsubscribed, or you turned them off.'}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm"
          disabled={busy}
          onClick={() => setEnabled(!client.aftercareEmails)}
        >
          {client.aftercareEmails ? 'Turn off' : 'Turn back on'}
        </button>
      </div>
      <ErrorBanner>{error}</ErrorBanner>
    </section>
  );
}
